import { useCallback, useState } from "react";
import { toast } from "@jsq/ds/core";
import { withClientContext } from "~/apollo/client";
import { NewsletterSubscriptionTypes } from "~/graphql/mainEnums";
import { useEditUserMutation } from "~/graphql/mainHooks";
import { toGlobalId } from "~/utils/graphql-helpers";

export type FormValues = { emailAddress: string };

export const useEditAccountSettings = ({
  userId,
  currentEmailAddress,
}: {
  userId?: string;
  currentEmailAddress?: string;
}) => {
  const [showModal, setShowModal] = useState(false);
  const [editUser, { loading: isSubmitting }] = useEditUserMutation({
    context: withClientContext("staff"),
  });

  const onHide = useCallback(() => {
    setShowModal(false);
  }, []);

  const openModal = useCallback(() => {
    setShowModal(true);
  }, []);

  const onSubmit = useCallback(
    async (values: Partial<FormValues>) => {
      if (!userId) {
        toast.error("User ID is required");
        return;
      }

      if (!values.emailAddress) {
        toast.error("Updated email address is required");
        return;
      }

      try {
        const globalUserId = toGlobalId("UserObjectType", userId);

        const response = await editUser({
          variables: {
            objectId: globalUserId,
            objectInput: {
              emailAddress: values.emailAddress.trim(),
              newsletterSubscribed: true,
              newsletterSubscriptionType: NewsletterSubscriptionTypes.Infrequent,
            },
          },
          refetchQueries: ["GetCcToolsUser", "GetCCToolsUsers"],
        });

        if (response.data?.userOps?.editUser?.user) {
          onHide();
          toast.success("Account settings updated successfully");
        } else {
          toast.error("There was an error updating account settings");
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
        toast.error(`Failed to update account settings: ${errorMessage}`);
      }
    },
    [editUser, userId, onHide],
  );

  const initialValues: FormValues = {
    emailAddress: currentEmailAddress || "",
  };

  return {
    initialValues,
    onHide,
    openModal,
    showModal,
    onSubmit,
    isSubmitting,
  };
};
